import type Scroll from 'quill/blots/scroll';
import QuillLink from 'quill/formats/link';
import { MOD_KEY } from '../../constants/os';
import safeParseUrl from '../../utils/url/safeParseUrl';
import { sanitzieQuillLink } from '../../utils/url/sanitizeUrl';
import { isOrganizationOrigin } from '../utils/matchCurrentUrl';

const { HOST } = window.__slab;

const setAttributes = (
  node: HTMLAnchorElement & { host?: string },
  defaultOpenInNewTab: boolean,
) => {
  const url = safeParseUrl(node.href);
  const shouldOpenInNewTab =
    defaultOpenInNewTab || !url || !isOrganizationOrigin(url);

  if (shouldOpenInNewTab) {
    node.setAttribute('target', '_blank');
    if (node.host !== HOST) {
      node.setAttribute('rel', 'nofollow noopener noreferrer ugc');
    }
  } else {
    node.removeAttribute('target');
    node.removeAttribute('rel');
  }
};

class Link extends QuillLink {
  protected static defaultNewTab = false;
  static className = 'ql-link';

  static create(value: string) {
    const node = super.create(value);
    setAttributes(node as HTMLAnchorElement, this.defaultNewTab);
    return node;
  }

  static sanitize(url: string) {
    return sanitzieQuillLink(url) ?? 'about:blank';
  }

  constructor(scroll: Scroll, domNode: HTMLElement) {
    super(scroll, domNode);

    domNode.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;

      const href = this.statics.formats(this.domNode);
      if (typeof href !== 'string') return;

      const isPressingModKey = event[MOD_KEY];
      const readOnly = !scroll.isEnabled();

      if (isPressingModKey && !readOnly && href) {
        window.open(href, '_blank', 'noopener');
        event.preventDefault();
      }
    });
  }

  format(name: string, value: string) {
    super.format(name, value);
    if (name === Link.blotName && value) {
      setAttributes(
        this.domNode as HTMLAnchorElement,
        this.statics.defaultNewTab,
      );
    }
  }
}

export default Link;
